import { style, styleVariants, globalStyle } from '@vanilla-extract/css'
import { vars, focusRing, dot as dotBase } from '../design/theme.css'
import { reading, sectionHead, utility } from '../design/primitives/text.css'
import { ruledList, ruledRow, ruledLabel, ruledValue } from '../design/primitives/layout.css'

// THE STATION CONSOLE — promoted from a right-docking drawer to a full-width workspace that
// takes over the page below the masthead: a header, a section rail, and a working pane. Two
// kinds of thing only — what an admin can do, and what fails quietly — with Broadcast and the
// Danger zone set apart behind their own rules. Controls are design-system primitives
// (Button / Field / Select / Textarea / SegmentedControl); this module owns the structure.

export const workspace = style({
  position: 'fixed',
  left: 0,
  right: 0,
  top: vars.layout.mastH,
  bottom: 0,
  zIndex: vars.z.overlay,
  background: vars.color.paper,
  overflowY: 'auto',
})
// A centred work column, roomier than a drawer but not full-bleed.
export const inner = style({
  maxWidth: vars.layout.pageMax,
  margin: '0 auto',
  padding: `${vars.space[5]} ${vars.space[6]} ${vars.space[9]}`,
})

export const header = style({
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: vars.space[3],
  paddingBottom: vars.space[4],
  marginBottom: vars.space[5],
  borderBottom: `2px solid ${vars.color.ink}`,
})
export const headerLeft = style({ display: 'flex', flexDirection: 'column', gap: vars.space[1], minWidth: 0 })
export const back = style({ alignSelf: 'flex-start' })
export const title = style({
  margin: 0,
  fontFamily: vars.font.serif,
  fontWeight: 500,
  fontSize: vars.fs['2xl'],
  lineHeight: vars.lh.tight,
  color: vars.color.ink,
})

// A flash rides the top of the workspace so an action's result is readable from wherever
// you clicked.
const flashBase = {
  position: 'sticky',
  top: vars.space[2],
  zIndex: vars.z.base,
  margin: `0 0 ${vars.space[4]}`,
  padding: `${vars.space[2]} ${vars.space[3]}`,
  borderRadius: vars.radius.sm,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
} as const
export const flash = styleVariants({
  ok: {
    ...flashBase,
    color: vars.color.ink,
    background: vars.color.paper2,
    border: `1px solid ${vars.color.rule}`,
  },
  alert: {
    ...flashBase,
    color: vars.color.red,
    background: vars.color.paper,
    border: `1px solid ${vars.color.red}`,
  },
})

export const lead = style({
  display: 'flex',
  alignItems: 'center',
  gap: vars.space[2],
  flexWrap: 'wrap',
  color: vars.color.ink2,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  margin: 0,
})

// The console: a section rail beside the pane.
export const console_ = style({
  display: 'grid',
  gridTemplateColumns: '150px minmax(0, 1fr)',
  gap: vars.space[6],
  '@media': { 'screen and (max-width: 700px)': { gridTemplateColumns: '1fr', gap: vars.space[4] } },
})
export const nav = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.space[1],
  '@media': { 'screen and (max-width: 700px)': { flexDirection: 'row', flexWrap: 'wrap' } },
})
// The section rail. The live section is marked the way every other live option in the app is
// marked — inked and underlined — rather than by a shaded block only this control used.
const navBase = {
  background: 'transparent',
  border: 0,
  textAlign: 'left',
  cursor: 'pointer',
  padding: `${vars.space[2]} 0`,
  minHeight: vars.ctrl.lg,
  display: 'flex',
  alignItems: 'center',
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  lineHeight: vars.lh.snug,
  textTransform: 'uppercase',
  selectors: { '&:focus-visible': focusRing },
} as const
export const navItem = styleVariants({
  on: {
    ...navBase,
    color: vars.color.ink,
    textDecoration: 'underline',
    textUnderlineOffset: vars.uline.mark,
  },
  off: {
    ...navBase,
    color: vars.color.inkSoft,
    selectors: { ...navBase.selectors, '&:hover': { color: vars.color.ink2 } },
  },
})
export const pane = style({})
export const paneTitle = style([
  sectionHead,
  {
    marginBottom: vars.space[4],
    paddingBottom: vars.space[2],
    borderBottom: `1px solid ${vars.color.rule}`,
  },
])

// One control per field: name · a line of sense · the control — parted by hairlines.
export const field = style({
  selectors: {
    '& + &': {
      marginTop: vars.space[5],
      paddingTop: vars.space[5],
      borderTop: `1px solid ${vars.color.rule}`,
    },
  },
})
export const fieldLabel = style({
  margin: 0,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  color: vars.color.ink,
})
export const fieldHint = style({
  margin: `${vars.space[1]} 0 0`,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
})
export const control = style({
  marginTop: vars.space[3],
  display: 'flex',
  gap: vars.space[2],
  alignItems: 'center',
  flexWrap: 'wrap',
})
// The inputs in a control row should size to content, not stretch full width.
export const inputInline = style({ flex: '0 1 auto', minWidth: vars.measure.narrow, width: 'auto' })
export const inputFull = style({ width: '100%' })
export const noteBox = style({
  width: '100%',
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
})
export const blastPreview = style({
  width: '100%',
  height: '60vh',
  marginTop: vars.space[2],
  border: `1px solid ${vars.color.rule}`,
  borderRadius: vars.radius.sm,
  background: vars.color.paper,
})

export const mt = style({ marginTop: vars.space[4] })

// Broadcast + Danger — set apart behind their own rule, in the GitHub idiom: restrained red.
export const zone = style({
  border: `1px solid ${vars.color.red}`,
  borderRadius: vars.radius.sm,
  padding: `${vars.space[4]} ${vars.space[4]}`,
})
export const zoneH = style([sectionHead, { marginBottom: vars.space[3], color: vars.color.red }])

// Instrument read-out — status dot, absolute stamp, quiet note.
export const dot = styleVariants({
  fresh: { ...dotBase, background: vars.color.green },
  quiet: { ...dotBase, background: vars.color.amber },
  stale: { ...dotBase, background: vars.color.inkSoft },
  none: { ...dotBase, background: vars.color.inkSoft },
})
export const abs = style([reading, { fontSize: vars.ms.sm, color: vars.color.inkSoft }])
export const noteLine = style({
  color: vars.color.inkSoft,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  fontStyle: 'italic',
  margin: `${vars.space[2]} 0 0`,
})
export const warn = style({ color: vars.color.amber })

// Two big figures.
export const stats = style({
  display: 'flex',
  flexWrap: 'wrap',
  gap: vars.space[6],
  marginTop: vars.space[4],
})
export const stat = style({ display: 'flex', flexDirection: 'column', gap: vars.space[1] })
export const fig = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.xl,
  lineHeight: 1,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.ink,
})
// The caption under an overview figure — the same job as the species sheet's stat label and
// Stats' summary label, which were 10px, 10px and 11px for one role.
export const lbl = utility

// The silent-failure feed — a quiet amber-ruled panel, shown only when something is wrong.
export const faults = style({
  marginTop: vars.space[4],
  padding: `${vars.space[3]} ${vars.space[3]}`,
  border: `1px solid ${vars.color.amber}`,
  borderRadius: vars.radius.sm,
})
export const faultsHead = style([
  sectionHead,
  {
    marginBottom: vars.space[2],
    color: vars.color.amber,
    display: 'flex',
    alignItems: 'center',
    gap: vars.space[2],
  },
])
export const faultsN = style({ fontVariantNumeric: 'tabular-nums' })
export const faultsList = style({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: vars.space[2],
})
export const fault = style({ display: 'flex', flexDirection: 'column' })
export const faultArea = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  color: vars.color.ink,
  fontWeight: 500,
})
export const faultCount = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  color: vars.color.inkSoft,
  fontWeight: 400,
})
export const faultMsg = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.sm,
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
  wordBreak: 'break-word',
})

// Ruled label/value rows (vitals + overview read-outs) — the account drawer's settings rows,
// shared rather than copied.
export const rules = style([ruledList, { marginTop: vars.space[4] }])
export const rule = ruledRow
export const ruleLabel = ruledLabel
export const ruleValue = ruledValue
globalStyle(`${rules}:first-child`, { marginTop: 0 })

// ---- The notes desk (Admin → Notes) ------------------------------------
// The station's own writing, listed for editing. Drafts are readable HERE AND NOWHERE ELSE, so the
// list has to say which is which at a glance — hence the tag rather than a subtler cue.
export const noteList = style({
  listStyle: 'none',
  margin: `${vars.space[3]} 0 0`,
  padding: 0,
  borderTop: `1px solid ${vars.color.rule}`,
})
export const noteRow = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: vars.space[3],
  width: '100%',
  appearance: 'none',
  background: 'transparent',
  border: 0,
  borderBottom: `1px solid ${vars.color.rule}`,
  cursor: 'pointer',
  textAlign: 'left',
  padding: `${vars.space[2]} 0`,
  selectors: { '&:hover': { background: vars.color.paper2 } },
})
export const noteRowOn = style([noteRow, { background: vars.color.paper2 }])
export const noteRowTitle = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  color: vars.color.ink,
  minWidth: 0,
})
export const noteRowMeta = style([utility, { flex: 'none' }])
// A draft is the one state that changes what the reader sees, so it is the one that gets colour.
export const noteRowDraft = style([noteRowMeta, { color: vars.color.amber }])
export const noteBody = style({
  width: '100%',
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  lineHeight: vars.lh.relaxed,
})

// ---- Bluesky (Admin → Bluesky) -----------------------------------------
// Three streams, each needing the same three things said: whether it is on, what it would post
// right now, and what it last posted. The structure carries that order, because at the
// switchover the question is always asked in it.

// The post as it would appear. Serif at reading size and `pre-wrap`, because this is 300
// characters of someone's own writing and a post whose line breaks had been reflowed away would
// be a preview of something other than the post.
export const post = style({
  margin: `${vars.space[3]} 0 0`,
  padding: `${vars.space[3]} ${vars.space[4]}`,
  border: `1px solid ${vars.color.rule}`,
  borderRadius: vars.radius.sm,
  background: vars.color.paper2,
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.relaxed,
  color: vars.color.ink,
  whiteSpace: 'pre-wrap',
})
// The link card under it — the title, description and whether a thumbnail was actually found.
export const card = style({
  display: 'flex',
  flexDirection: 'column',
  gap: vars.space[1],
  margin: `${vars.space[2]} 0 0`,
  paddingLeft: vars.space[4],
  borderLeft: `2px solid ${vars.color.rule}`,
})
export const cardTitle = style({ fontFamily: vars.font.serif, fontSize: vars.fs.sm, color: vars.color.ink })
export const cardDesc = style({
  fontFamily: vars.font.serif,
  fontSize: vars.fs.sm,
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
})
export const cardLink = style([
  reading,
  { fontSize: vars.ms.sm, color: vars.color.inkSoft, wordBreak: 'break-all' },
])

// The ledger — what the stream last said. Dates and refs are data, so they sit in the reading
// face and align; the outcome is a word, because "published" against "handled, nothing posted"
// is the distinction the whole row exists to make and a tick would collapse it.
export const ledger = style({
  listStyle: 'none',
  margin: `${vars.space[3]} 0 0`,
  padding: 0,
  borderTop: `1px solid ${vars.color.rule}`,
})
export const ledgerRow = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  gap: vars.space[3],
  padding: `${vars.space[2]} 0`,
  borderBottom: `1px solid ${vars.color.rule}`,
})
export const ledgerRef = style([
  reading,
  { fontSize: vars.ms.sm, color: vars.color.ink, flex: '1 1 auto', minWidth: 0, wordBreak: 'break-word' },
])
export const ledgerWhen = style([utility, { flex: 'none', color: vars.color.inkSoft }])
// Both outcomes are correct ones, so neither is coloured as a fault: a stream that looked and
// decided not to post is doing its job, and the amber here would mean a problem that isn't there.
export const ledgerState = style([utility, { flex: 'none', color: vars.color.inkSoft }])
export const ledgerSaid = style([utility, { flex: 'none', color: vars.color.green }])
export const empty = style([
  utility,
  { margin: `${vars.space[3]} 0 0`, color: vars.color.inkSoft, display: 'block' },
])
